/**
 * The snapshot price timeline.
 *
 * ── Why price is not a fact row ──────────────────────────────────────────────
 *
 * Price is deliberately absent from `FACT_ATTRIBUTES`. A fact row would hold
 * one current value and lose the history — and the history is the whole of the
 * negotiation context: `motivationBps` is built from the number of prior
 * reductions and the days listed, and "the seller has dropped it twice" is the
 * single most useful sentence anybody can carry into a phone call.
 *
 * `snapshots.capture()` writes nothing when the watched hash is unchanged, so
 * every row here is a real change rather than a poll that happened. The
 * baseline (`seq = 1`) is included so the first price we ever saw is on the
 * chart even though it was not a reduction.
 *
 * The bars are a proportion of the highest price ever recorded, drawn in
 * divs — a chart library for eight rows on a phone is twelve kilobytes to say
 * "it went down".
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import { dateText, delta, money, TONES } from '../../lib/format'
import { Chip, Empty, Panel, S } from './ui'
import type { PricePoint } from './types'

const EN = {
  title: 'Price history',
  none: 'Only one price has ever been recorded.',
  noneWhy: 'A row appears here each time the asking price actually changes.',
  unavailable: 'The price history could not be loaded.',
  first: 'First price we saw',
  drops: 'reductions',
  total: 'Down from the first price',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Historial de precio',
  none: 'Solo se ha registrado un precio.',
  noneWhy: 'Aparece una línea aquí cada vez que el precio pedido cambia de verdad.',
  unavailable: 'No se pudo cargar el historial de precio.',
  first: 'Primer precio que vimos',
  drops: 'bajadas',
  total: 'Bajó desde el primer precio',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

export default function PriceTimeline({
  points,
  failed = false,
}: {
  points: PricePoint[]
  failed?: boolean
}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const priced = points.filter((point): point is PricePoint & { priceCents: number } => point.priceCents !== null)
  const highest = priced.reduce((max, point) => Math.max(max, point.priceCents), 0)
  const first = priced[0]
  const last = priced[priced.length - 1]
  const reductions = points.filter((point) => (point.deltaCents ?? 0) < 0).length
  const movement = first && last ? last.priceCents - first.priceCents : null

  return (
    <Panel
      id="price"
      title={t('title')}
      badge={reductions > 0 ? <Chip tone={TONES.money}>{`${reductions} ${t('drops')}`}</Chip> : undefined}
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : priced.length <= 1 ? (
        <>
          <Empty>{t('none')}</Empty>
          <p style={S.note}>{t('noneWhy')}</p>
          {first && (
            <p style={{ ...S.sentence, marginTop: 6 }}>
              {t('first')}: <strong>{money(first.priceCents, locale)}</strong>
            </p>
          )}
        </>
      ) : (
        <>
          {priced.map((point, index) => (
            <div key={point.seq} style={{ padding: '8px 0', borderTop: index === 0 ? 'none' : '1px solid var(--line)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ flex: 1, fontSize: 12, color: 'var(--muted)' }}>{dateText(point.capturedAt, locale)}</span>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{money(point.priceCents, locale)}</span>
                {point.deltaCents !== null && (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      minWidth: 64,
                      textAlign: 'right',
                      color: point.deltaCents < 0 ? 'var(--score-strong)' : 'var(--danger)',
                    }}
                  >
                    {delta(point.deltaCents, locale)}
                  </span>
                )}
              </div>
              <span
                aria-hidden="true"
                style={{ display: 'block', height: 4, borderRadius: 999, background: 'var(--line)', marginTop: 5 }}
              >
                <span
                  style={{
                    display: 'block',
                    height: '100%',
                    width: `${highest > 0 ? Math.round((point.priceCents / highest) * 100) : 0}%`,
                    background: 'var(--money)',
                  }}
                />
              </span>
            </div>
          ))}

          {movement !== null && movement !== 0 && (
            <p style={{ ...S.note, marginTop: 10 }}>
              {t('total')}: <strong>{delta(movement, locale)}</strong>
            </p>
          )}
        </>
      )}
    </Panel>
  )
}
